/**
 * Recipes of the sound effects: every SoundCue maps to a short list of voices (plain data,
 * see synth.ts). Design rules: short and soft for what happens all the time (jumps, orbs),
 * a little longer and brighter for rewards, never scary (the death sound is a cartoon
 * "bonk"), other racers' events quiet in the background.
 */
import type { AttackKind, BoostType, BossKind, Mode, PortalType } from '@cube-rush/shared';
import {
  type Envelope,
  held,
  type NoiseVoice,
  pluck,
  type ToneVoice,
  type VoiceSpec,
  type Wave,
} from './synth';
import type { SoundCue } from './types';

// Note frequencies (Hz) used by the jingles.
const C5 = 523.25;
const E5 = 659.25;
const G5 = 783.99;
const A5 = 880;
const B5 = 987.77;
const C6 = 1046.5;
const E6 = 1318.51;
const G6 = 1567.98;

interface ToneOptions {
  at?: number;
  to?: number;
  glide?: number;
}

function tone(
  wave: Wave,
  freq: number,
  gain: number,
  env: Envelope,
  options: ToneOptions = {},
): ToneVoice {
  const voice: ToneVoice = { kind: 'tone', wave, at: options.at ?? 0, freq, gain, env };
  if (options.to !== undefined) {
    voice.freqTo = options.to;
    voice.glide = options.glide ?? 0.1;
  }
  return voice;
}

interface NoiseOptions {
  at?: number;
  to?: number;
  q?: number;
}

function noise(
  filter: BiquadFilterType,
  freq: number,
  gain: number,
  env: Envelope,
  options: NoiseOptions = {},
): NoiseVoice {
  const voice: NoiseVoice = { kind: 'noise', at: options.at ?? 0, filter, freq, gain, env };
  if (options.to !== undefined) voice.freqTo = options.to;
  if (options.q !== undefined) voice.q = options.q;
  return voice;
}

/** A note of a jingle: band-limited square, held for `length` seconds. */
function note(
  freq: number,
  at: number,
  length: number,
  gain: number,
  wave: Wave = 'pulse25',
): ToneVoice {
  return tone(wave, freq, gain, held(length, 0.6, 0.08), { at });
}

const JUMP: Readonly<Record<Mode, readonly VoiceSpec[]>> = {
  cube: [tone('pulse25', 360, 0.2, pluck(0.09), { to: 640, glide: 0.06 })],
  ship: [
    tone('triangle', 300, 0.2, pluck(0.11), { to: 520, glide: 0.08 }),
    noise('bandpass', 1200, 0.05, pluck(0.08), { to: 2400, q: 1 }),
  ],
  ball: [
    tone('softSquare', 520, 0.14, pluck(0.06), { to: 390, glide: 0.05 }),
    tone('softSquare', 390, 0.14, pluck(0.07), { at: 0.05, to: 700, glide: 0.05 }),
  ],
  ufo: [tone('triangle', 260, 0.22, pluck(0.14), { to: 780, glide: 0.1 })],
  wave: [tone('pulse12', 880, 0.16, pluck(0.05), { to: 700, glide: 0.03 })],
  // A servo's click, a springy boing lower than the cube's, the jets' hiss rising with it.
  robot: [
    noise('bandpass', 1500, 0.06, pluck(0.04), { to: 700, q: 4 }),
    tone('pulse25', 200, 0.17, pluck(0.13), { to: 480, glide: 0.11 }),
    noise('bandpass', 600, 0.05, pluck(0.22), { to: 1800, q: 1.2 }),
  ],
  // A zap there and back: a quick fall and rise in pitch, a crackle on top.
  spider: [
    tone('softSaw', 1900, 0.12, pluck(0.07), { to: 700, glide: 0.05 }),
    tone('pulse12', 700, 0.1, pluck(0.07), { at: 0.03, to: 1600, glide: 0.04 }),
    noise('highpass', 3500, 0.05, pluck(0.05)),
  ],
};

const ORB: Readonly<Record<'yellow' | 'pink' | 'blue' | 'green', readonly VoiceSpec[]>> = {
  yellow: [
    tone('triangle', 880, 0.22, pluck(0.18), { to: 1320, glide: 0.05 }),
    tone('sine', 1760, 0.08, pluck(0.15), { at: 0.02 }),
  ],
  pink: [tone('triangle', 1046, 0.16, pluck(0.13), { to: 1397, glide: 0.04 })],
  blue: [tone('softSaw', 1200, 0.15, pluck(0.18), { to: 480, glide: 0.14 })],
  green: [
    tone('softSaw', 480, 0.14, pluck(0.16), { to: 1200, glide: 0.12 }),
    tone('triangle', 1046, 0.12, pluck(0.12), { at: 0.06 }),
  ],
};

const PAD: Readonly<Record<'yellow' | 'pink' | 'blue', readonly VoiceSpec[]>> = {
  yellow: [
    tone('triangle', 180, 0.26, pluck(0.22), { to: 900, glide: 0.16 }),
    noise('highpass', 3000, 0.05, pluck(0.03)),
  ],
  pink: [tone('triangle', 300, 0.2, pluck(0.15), { to: 760, glide: 0.1 })],
  blue: [tone('softSaw', 900, 0.14, pluck(0.18), { to: 300, glide: 0.14 })],
};

/** Base note of the "new vehicle" shimmer, one per mode. */
const MODE_BASE: Readonly<Record<Mode, number>> = {
  cube: C6,
  ship: 1174.66,
  ball: E6,
  ufo: G6,
  wave: 1760,
  robot: 1396.91,
  spider: 1975.53,
};

function portalVoices(portal: PortalType, slower: boolean): readonly VoiceSpec[] {
  if (portal === 'gravity-normal' || portal === 'gravity-reverse') {
    const [from, to] = portal === 'gravity-normal' ? [1100, 420] : [420, 1100];
    return [
      tone('softSaw', from, 0.16, pluck(0.26), { to, glide: 0.22 }),
      noise('lowpass', from * 2.7, 0.08, pluck(0.25), { to: to * 2.7 }),
    ];
  }
  if (portal.startsWith('mode-')) {
    const base = MODE_BASE[portal.slice(5) as Mode] ?? C6;
    return [
      tone('triangle', base, 0.12, pluck(0.16)),
      tone('triangle', base * 1.25, 0.12, pluck(0.16), { at: 0.05 }),
      tone('triangle', base * 1.5, 0.12, pluck(0.2), { at: 0.1 }),
      tone('sine', base * 2, 0.04, pluck(0.2), { at: 0.1 }),
    ];
  }
  // speed-0 .. speed-3: the faster the speed, the higher the zoom; a slow-down glides down
  // from where the faster speed's zoom would have ended, to the new speed's start.
  const speed = Number(portal.slice(6)) || 0;
  if (slower) {
    return [
      noise('bandpass', Math.min(8000, 2400 * (speed + 2)), 0.13, pluck(0.26), {
        to: 400 * (speed + 1),
        q: 2,
      }),
      tone('pulse25', 500 + 200 * (speed + 1), 0.15, pluck(0.24), {
        to: 200 + 80 * speed,
        glide: 0.22,
      }),
    ];
  }
  return [
    noise('bandpass', 400 * (speed + 1), 0.14, pluck(0.22), {
      to: Math.min(8000, 2400 * (speed + 1)),
      q: 2,
    }),
    tone('pulse25', 200 + 80 * speed, 0.16, pluck(0.2), { to: 500 + 200 * speed, glide: 0.18 }),
  ];
}

const DEATH: readonly VoiceSpec[] = [
  noise('lowpass', 3500, 0.26, pluck(0.32), { to: 250 }),
  tone('softSquare', 330, 0.15, pluck(0.28), { to: 70, glide: 0.25 }),
];

const RESPAWN: readonly VoiceSpec[] = [
  tone('sine', C5, 0.1, pluck(0.1)),
  tone('sine', E5, 0.1, pluck(0.1), { at: 0.04 }),
  tone('sine', G5, 0.1, pluck(0.12), { at: 0.08 }),
];

/** Major arpeggio and a chord, one semitone higher for every level: a little climb. */
function levelCompleteVoices(level: number): readonly VoiceSpec[] {
  const shift = 2 ** (Math.min(Math.max(0, Math.floor(level)), 9) / 12);
  const chord = (freq: number) =>
    tone('triangle', freq * shift, 0.08, held(0.25, 0.6, 0.15), { at: 0.28 });
  return [
    note(C5 * shift, 0, 0.06, 0.13),
    note(E5 * shift, 0.07, 0.06, 0.13),
    note(G5 * shift, 0.14, 0.06, 0.13),
    note(C6 * shift, 0.21, 0.06, 0.13),
    chord(C6),
    chord(E6),
    chord(G6),
  ];
}

/** Winner: a full fanfare; 2nd and 3rd: shorter ones; everybody else: a friendly "done!". */
function raceFinishVoices(place: number | null): readonly VoiceSpec[] {
  if (place === 1) {
    return [
      note(G5, 0, 0.08, 0.12),
      note(G5, 0.1, 0.08, 0.12),
      note(G5, 0.2, 0.08, 0.12),
      note(C6, 0.3, 0.32, 0.13),
      tone('triangle', E5, 0.06, held(0.32, 0.6, 0.1), { at: 0.3 }),
      note(A5, 0.7, 0.1, 0.12),
      note(B5, 0.84, 0.1, 0.12),
      note(C6, 0.98, 0.5, 0.13),
      tone('triangle', E6, 0.07, held(0.5, 0.6, 0.2), { at: 0.98 }),
      tone('triangle', G6, 0.06, held(0.5, 0.6, 0.2), { at: 0.98 }),
      tone('softSquare', 130.81, 0.1, held(0.5, 0.7, 0.2), { at: 0.98 }),
      noise('bandpass', 2000, 0.04, pluck(0.3), { at: 0.98, q: 0.7 }),
      tone('sine', 2093, 0.03, pluck(0.4), { at: 1.2 }),
    ];
  }
  if (place === 2) {
    return [
      note(E5, 0, 0.1, 0.12),
      note(G5, 0.14, 0.1, 0.12),
      note(C6, 0.3, 0.4, 0.13),
      tone('triangle', E6, 0.06, held(0.4, 0.6, 0.15), { at: 0.3 }),
    ];
  }
  if (place === 3) {
    return [
      note(G5, 0, 0.09, 0.12),
      note(C6, 0.12, 0.36, 0.12),
      tone('triangle', E5, 0.06, held(0.36, 0.6, 0.15), { at: 0.12 }),
    ];
  }
  return [
    tone('triangle', C5, 0.1, pluck(0.3)),
    tone('triangle', E5, 0.1, pluck(0.3), { at: 0.1 }),
    tone('triangle', G5, 0.1, pluck(0.35), { at: 0.2 }),
  ];
}

const PICKUP: Readonly<Record<BoostType, readonly VoiceSpec[]>> = {
  turbo: [
    tone('softSaw', 400, 0.14, pluck(0.14), { to: 1600, glide: 0.1 }),
    tone('pulse12', 1600, 0.06, pluck(0.08), { at: 0.08 }),
  ],
  shield: [
    tone('triangle', E6, 0.12, pluck(0.3)),
    tone('triangle', 1975.53, 0.08, pluck(0.25), { at: 0.02 }),
    tone('sine', 2637.02, 0.03, pluck(0.2), { at: 0.02 }),
  ],
  doubleJump: [
    tone('pulse25', 660, 0.13, pluck(0.06)),
    tone('pulse25', 990, 0.13, pluck(0.08), { at: 0.07 }),
  ],
  slowmo: [
    tone('triangle', 700, 0.16, pluck(0.24), { to: 350, glide: 0.2 }),
    tone('triangle', 525, 0.08, pluck(0.22), { at: 0.03, to: 262, glide: 0.2 }),
  ],
};

/** The inventory was full: a dull double "bonk". */
const PICKUP_IGNORED: readonly VoiceSpec[] = [
  tone('softSquare', 160, 0.1, pluck(0.07)),
  tone('softSquare', 120, 0.08, pluck(0.06), { at: 0.05 }),
];

const BOOST_START: Readonly<Record<BoostType, readonly VoiceSpec[]>> = {
  turbo: [
    tone('softSaw', 180, 0.14, pluck(0.34), { to: 1200, glide: 0.3 }),
    noise('bandpass', 600, 0.07, pluck(0.32), { to: 5000, q: 1.5 }),
    tone('pulse25', 1200, 0.08, pluck(0.12), { at: 0.28 }),
  ],
  shield: [
    tone('triangle', E6, 0.1, pluck(0.35)),
    tone('triangle', 1661.22, 0.1, pluck(0.35), { at: 0.05 }),
    tone('triangle', 1975.53, 0.1, pluck(0.35), { at: 0.1 }),
    tone('triangle', 2637.02, 0.08, pluck(0.35), { at: 0.15 }),
  ],
  doubleJump: [
    tone('pulse25', 523, 0.14, pluck(0.1), { to: 784, glide: 0.08 }),
    tone('pulse25', 784, 0.14, pluck(0.12), { at: 0.09, to: 1046, glide: 0.08 }),
  ],
  slowmo: [
    tone('softSaw', 900, 0.13, pluck(0.42), { to: 260, glide: 0.4 }),
    noise('lowpass', 2000, 0.05, pluck(0.4), { to: 300 }),
  ],
};

const BOOST_END_BASE: Readonly<Record<BoostType, number>> = {
  turbo: 900,
  shield: E6,
  doubleJump: 784,
  slowmo: 600,
};

const COUNTDOWN_BEEP: readonly VoiceSpec[] = [
  tone('softSquare', C5, 0.14, held(0.12, 0.7, 0.05)),
  tone('triangle', C6, 0.05, pluck(0.12)),
];

const COUNTDOWN_GO: readonly VoiceSpec[] = [
  tone('softSquare', C6, 0.15, held(0.32, 0.7, 0.1)),
  tone('triangle', G6, 0.08, held(0.32, 0.7, 0.1)),
  tone('triangle', G5, 0.06, held(0.32, 0.7, 0.1)),
];

/**
 * The time is up: a short horn of two notes going down, round and warm (not a buzzer: nobody
 * did anything wrong), clearly not a countdown beep or a fanfare.
 */
const TIME_UP: readonly VoiceSpec[] = [
  tone('softSquare', G5, 0.13, held(0.2, 0.75, 0.06)),
  tone('triangle', G5 / 2, 0.08, held(0.2, 0.75, 0.06)),
  tone('softSquare', C5, 0.14, held(0.42, 0.7, 0.3), { at: 0.24, to: C5 * 0.94, glide: 0.5 }),
  tone('triangle', C5 / 2, 0.09, held(0.42, 0.7, 0.3), { at: 0.24 }),
];

const OPPONENT_LEVEL: readonly VoiceSpec[] = [
  tone('sine', E6, 0.035, pluck(0.12)),
  tone('sine', C6, 0.03, pluck(0.14), { at: 0.08 }),
];

const OPPONENT_FINISH: readonly VoiceSpec[] = [
  tone('sine', C6, 0.035, pluck(0.16)),
  tone('sine', E6, 0.035, pluck(0.16), { at: 0.07 }),
  tone('sine', G6, 0.035, pluck(0.18), { at: 0.14 }),
];

const UI_TICK: readonly VoiceSpec[] = [tone('triangle', 1400, 0.1, pluck(0.035))];

/** A blade slipped past: a soft metallic swish, rising and falling away (never a scare). */
const BLADE: readonly VoiceSpec[] = [
  noise('bandpass', 1800, 0.06, pluck(0.16), { to: 5200, q: 3 }),
  tone('triangle', 2400, 0.035, pluck(0.12), { to: 1600, glide: 0.1 }),
];

/**
 * A boss's roar as its level begins: a big cartoon growl, down in pitch, never a scream — the
 * dragon's breathy, the robot's an engine revving with a servo's whine and two beeps — and a
 * shorter one when it comes back for a new attempt.
 */
const BOSS_ROAR: Readonly<Record<BossKind, readonly VoiceSpec[]>> = {
  dragon: [
    tone('softSaw', 150, 0.2, held(0.5, 0.7, 0.25), { to: 82, glide: 0.65 }),
    tone('softSquare', 225, 0.08, held(0.4, 0.6, 0.2), { to: 120, glide: 0.55 }),
    noise('bandpass', 520, 0.16, held(0.5, 0.6, 0.25), { to: 170, q: 1.4 }),
  ],
  robot: [
    tone('softSquare', 92, 0.18, held(0.55, 0.7, 0.2), { to: 138, glide: 0.4 }),
    noise('bandpass', 900, 0.08, held(0.45, 0.5, 0.2), { to: 2600, q: 3 }),
    tone('pulse25', 660, 0.07, pluck(0.09), { at: 0.6 }),
    tone('pulse25', 880, 0.07, pluck(0.11), { at: 0.72 }),
  ],
};

const BOSS_GROWL: Readonly<Record<BossKind, readonly VoiceSpec[]>> = {
  dragon: [
    tone('softSaw', 130, 0.15, pluck(0.35), { to: 85, glide: 0.3 }),
    noise('bandpass', 420, 0.1, pluck(0.3), { to: 180, q: 1.4 }),
  ],
  robot: [
    tone('softSquare', 100, 0.14, pluck(0.3), { to: 130, glide: 0.2 }),
    tone('pulse25', 660, 0.06, pluck(0.08), { at: 0.22 }),
  ],
};

/** The boss crashes behind as the player escapes it: a tumble, a thump and a happy little chord. */
function bossDefeatVoices(boss: BossKind): readonly VoiceSpec[] {
  const fall =
    boss === 'dragon'
      ? tone('softSaw', 300, 0.12, pluck(0.45), { to: 70, glide: 0.4 })
      : tone('pulse25', 700, 0.1, pluck(0.45), { to: 60, glide: 0.42 });
  return [
    fall,
    noise('lowpass', 1400, 0.2, pluck(0.35), { at: 0.32, to: 120 }),
    tone('sine', 70, 0.16, pluck(0.3), { at: 0.32 }),
    note(G5, 0.62, 0.08, 0.11),
    note(C6, 0.72, 0.08, 0.11),
    note(E6, 0.82, 0.24, 0.12),
    tone('triangle', G6, 0.05, held(0.24, 0.6, 0.12), { at: 0.82 }),
  ];
}

/**
 * A boss's attack: its warning — the dragon drawing breath, the ground rumbling, a laser charging,
 * an alarm's two beeps — then the attack itself: a fireball's falling whistle, a burst of flame, a
 * laser's zap, a rocket's whoosh.
 */
const ATTACK_WARN: Readonly<Record<AttackKind, readonly VoiceSpec[]>> = {
  fireball: [noise('bandpass', 300, 0.08, held(0.24, 0.6, 0.06), { to: 1300, q: 1.5 })],
  flame: [
    noise('lowpass', 260, 0.12, held(0.26, 0.6, 0.08), { to: 140 }),
    tone('sine', 55, 0.08, held(0.26, 0.6, 0.08)),
  ],
  laser: [tone('triangle', 520, 0.06, held(0.32, 0.7, 0.05), { to: 1500, glide: 0.34 })],
  rocket: [
    tone('pulse25', 1050, 0.07, pluck(0.07)),
    tone('pulse25', 1050, 0.07, pluck(0.07), { at: 0.13 }),
  ],
};

const ATTACK_GO: Readonly<Record<AttackKind, readonly VoiceSpec[]>> = {
  fireball: [
    tone('triangle', 1900, 0.06, pluck(0.38), { to: 450, glide: 0.36 }),
    noise('bandpass', 1600, 0.04, pluck(0.35), { to: 600, q: 2 }),
  ],
  flame: [
    noise('bandpass', 500, 0.15, pluck(0.32), { to: 2200, q: 1.1 }),
    noise('lowpass', 900, 0.14, pluck(0.2), { to: 150 }),
  ],
  laser: [
    tone('pulse12', 1800, 0.08, pluck(0.18), { to: 950, glide: 0.12 }),
    noise('highpass', 4200, 0.05, pluck(0.14)),
  ],
  rocket: [
    noise('bandpass', 700, 0.12, pluck(0.36), { to: 3200, q: 1.6 }),
    tone('softSaw', 240, 0.06, pluck(0.3), { to: 180, glide: 0.3 }),
  ],
};

/** The voices of a cue. Recipes are static data; only the jingles depend on numbers. */
export function cueVoices(cue: SoundCue): readonly VoiceSpec[] {
  switch (cue.kind) {
    case 'jump':
      return JUMP[cue.mode];
    case 'orb':
      return ORB[cue.color];
    case 'pad':
      return PAD[cue.color];
    case 'portal':
      return portalVoices(cue.portal, cue.slower === true);
    case 'death':
      return DEATH;
    case 'respawn':
      return RESPAWN;
    case 'levelComplete':
      return levelCompleteVoices(cue.level);
    case 'raceFinish':
      return raceFinishVoices(cue.place);
    case 'pickup':
      return cue.ignored ? PICKUP_IGNORED : PICKUP[cue.boost];
    case 'boostStart':
      return BOOST_START[cue.boost];
    case 'boostEnd': {
      const base = BOOST_END_BASE[cue.boost];
      return [tone('triangle', base, 0.12, pluck(0.15), { to: base / 2, glide: 0.12 })];
    }
    case 'countdown':
      return cue.secondsLeft <= 0 ? COUNTDOWN_GO : COUNTDOWN_BEEP;
    case 'timeUp':
      return TIME_UP;
    case 'opponent':
      return cue.event === 'finish' ? OPPONENT_FINISH : OPPONENT_LEVEL;
    case 'ui':
      return UI_TICK;
    case 'blade':
      return BLADE;
    case 'boss':
      if (cue.event === 'roar') return BOSS_ROAR[cue.boss];
      return cue.event === 'growl' ? BOSS_GROWL[cue.boss] : bossDefeatVoices(cue.boss);
    case 'attack':
      return cue.warn ? ATTACK_WARN[cue.attack] : ATTACK_GO[cue.attack];
    default:
      cue satisfies never;
      return [];
  }
}

/**
 * Identity of a cue for rate limiting: the same sound requested again within a few
 * milliseconds (e.g. two events in one frame) is played once.
 */
export function cueKey(cue: SoundCue): string {
  switch (cue.kind) {
    case 'jump':
      return `jump:${cue.mode}`;
    case 'orb':
    case 'pad':
      return `${cue.kind}:${cue.color}`;
    case 'portal':
      return `portal:${cue.portal}${cue.slower ? ':slower' : ''}`;
    case 'pickup':
      return `pickup:${cue.boost}:${cue.ignored}`;
    case 'boostStart':
    case 'boostEnd':
      return `${cue.kind}:${cue.boost}`;
    case 'countdown':
      return `countdown:${cue.secondsLeft}`;
    case 'opponent':
      return `opponent:${cue.event}`;
    case 'boss':
      return `boss:${cue.boss}:${cue.event}`;
    case 'attack':
      return `attack:${cue.attack}:${cue.warn ? 'warn' : 'go'}`;
    default:
      return cue.kind;
  }
}
